<script>
    import { createEventDispatcher, getContext } from "svelte";
    import { fly } from "svelte/transition";

    import Loader from "./Loader.svelte";
    import SongItem from "./SongItem.svelte";

    import { determineCatalogAlbumId, fetchMediaItems, getArtworkURL, invertColor } from "../utils.js";

    import { TOASTS_KEY } from "./toasts/toast.js";

    export let id;
    export let type;

    const dispatch = createEventDispatcher();

    const { addToast, addPromiseToast } = getContext(TOASTS_KEY);

    const music = MusicKit.getInstance();

    let attrs;
    let artworkUrl;
    let songs;
    let minimal;
    let subtitle;
    let isLibrary;
    let catalogAlbum;
    let colors = {
        modal_background: "var(--app-bg-color)",
        fg_color: "var(--app-fg-color)",
        bg_color: "var(--app-bg-color)",
        secondary_fg_color: "var(--app-secondary-fg-color)",
        secondary_bg_color: "var(--app-secondary-bg-color)",
        hover_secondary_fg_color: "var(--app-inverse-secondary-fg-color)",
        hover_secondary_bg_color: "var(--app-inverse-secondary-bg-color)",
    };
    let item = (async () => {
        const data = await (async () => {
            switch (type) {
                case "albums":
                    return (await music.api.music(`/v1/catalog/{{storefrontId}}/albums/${id}`)).data.data[0];
                case "library-albums":
                    return (await music.api.music(`/v1/me/library/albums/${id}`)).data.data[0];
                case "playlists":
                    return (await music.api.music(`/v1/catalog/{{storefrontId}}/playlists/${id}`)).data.data[0];
                case "library-playlists":
                    return (await music.api.music(`/v1/me/library/playlists/${id}`)).data.data[0];
                case "songs": {
                    const { data: { data: [song] } } = await music.api.music(`/v1/catalog/{{storefrontId}}/songs/${id}`, { include: "albums" });
                    const albumId = song.relationships.albums.data[0].id;
                    return (await music.api.music(`/v1/catalog/{{storefrontId}}/albums/${albumId}`)).data.data[0];
                }
                case "library-songs": {
                    const { data: { data: [song] } } = await music.api.music(`/v1/me/library/songs/${id}`, { include: "albums" });
                    const albumId = song.relationships.albums.data[0].id;
                    return (await music.api.music(`/v1/me/library/albums/${albumId}`)).data.data[0];
                }
            }
        })();
        
        attrs = data.attributes;

        artworkUrl = getArtworkURL(data, 400, 400) || '/missing-artwork.svg';
        songs = await fetchMediaItems(data.type, data.id);
        
        console.log(songs);

        minimal = (data.type == "albums" || data.type == "library-albums");
        isLibrary = attrs?.playParams?.isLibrary ?? false;

        subtitle = (() => {
            switch (type) {
                case "albums":
                case "library-albums":
                case "songs":
                case "library-songs":
                    return attrs.artistName;
                case "playlists":
                    return "Playlist";
                case "library-playlists":
                    return "Personal playlist";
            }
        })();

        if ((data.type === "albums" || data.type === "library-albums") && isLibrary) {
            (async () => {
                try {
                    const found = await determineCatalogAlbumId(data);
                    if (found !== undefined) {
                        catalogAlbum = found;
                    }
                } catch (err) {
                    // console.error(err);
                }
            })();
        }

        if (artworkUrl !== undefined && artworkUrl !== "/missing-artwork.svg") {
            try {
                const vibrant = Vibrant.from(artworkUrl).quality(1);
                const palette = await vibrant.getPalette();
                const color = palette.DarkVibrant.hex;
                const inverted = invertColor(color, true);
                const light = {
                    bg_color: "var(--app-light-bg-color)",
                    secondary_fg_color: "var(--app-light-secondary-fg-color)",
                    secondary_bg_color: "var(--app-light-secondary-bg-color)",
                    hover_secondary_fg_color: "var(--app-dark-secondary-fg-color)",
                    hover_secondary_bg_color: "var(--app-dark-secondary-bg-color)",
                };
                const dark = {
                    bg_color: "var(--app-dark-bg-color)",
                    secondary_fg_color: "var(--app-dark-secondary-fg-color)",
                    secondary_bg_color: "var(--app-dark-secondary-bg-color)",
                    hover_secondary_fg_color: "var(--app-light-secondary-fg-color)",
                    hover_secondary_bg_color: "var(--app-light-secondary-bg-color)",
                };
                const chosen = (inverted === "#E0E0E0") ? dark : light;
                colors = {
                    modal_background: color,
                    fg_color: inverted,
                    ...chosen,
                };
            } catch (err) {
                console.error(err);
            }
        }

        return data;
    })()
    .catch((err) => {
        console.error(err);
        throw err;
    });

    function handleAlbumPlay() {
        const promise = (async () => {
            music.shuffleMode = 0;
            await music.setQueue({ items: songs.map(song => ({ ...song, _container: { ...song } })) });
            await music.changeToMediaAtIndex(0);
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Scheduling the collection to play now ...",
            },
            success: {
                content: "The collection is now playing !",
                duration: 5000,
            },
            error: {
                content: "An error happened while attempting to schedule the collection.",
                duration: 5000,
            },
        });
    }

    function handleAlbumShuffle() {
        const promise = (async () => {
            music.shuffleMode = 1;
            await music.setQueue({ items: songs.map(song => ({ ...song, _container: { ...song } })) });
            await music.changeToMediaAtIndex(0);
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Scheduling the collection to play now ...",
            },
            success: {
                content: "The collection is now playing in shuffled order !",
                duration: 5000,
            },
            error: {
                content: "An error happened while attempting to schedule the collection.",
                duration: 5000,
            },
        });
    }

    function handleItemClick(song, idx) {
        const promise = (async () => {
            await music.setQueue({ items: songs.map(song => ({ ...song, _container: { ...song } })) });
            await music.changeToMediaAtIndex(idx);
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Scheduling the item to play now ...",
            },
            success: {
                content: "The item is now playing !",
                duration: 5000,
            },
            error: {
                content: "An error happened while attempting to schedule the item.",
                duration: 5000,
            },
        });
    }

    function handleCatalogAlbumClick() {
        if (catalogAlbum !== undefined) {
            dispatch("open-modal", { id: catalogAlbum.id, type: catalogAlbum.type });
        }
    }

    function handleItemContextMenu(song) {
        return function (event) {
            const { pageX, pageY, target } = event.detail;
            dispatch("context-menu", { item: song, pageX, pageY, target })
        };
    }
</script>

<div class="overlay" on:click|stopPropagation={() => dispatch("dismiss")}>
{#await item}
    <Loader />
{:then item}
    <div
        class="grid"
        style="
            --chosen-fg-color: {colors.fg_color};
            --chosen-bg-color: {colors.bg_color};
            --chosen-modal-color: {colors.modal_background};
            --chosen-secondary-fg-color: {colors.secondary_fg_color};
            --chosen-secondary-bg-color: {colors.secondary_bg_color};
            --chosen-hover-secondary-fg-color: {colors.hover_secondary_fg_color};
            --chosen-hover-secondary-bg-color: {colors.hover_secondary_bg_color};
        "
        in:fly={{ x: -10, duration: 250 }}
        out:fly={{ x: 10, duration: 250 }}
    >
        <div class="art">
            <img src="{artworkUrl}"
                 alt="{attrs.name}">
        </div>
        <div class="content">
            <div class="infos">
                <div class="title" title="{attrs.name}">{attrs.name}</div>
                <div class="artist" title="{subtitle}">{subtitle}</div>
            </div>
            <div class="controls">
                <div class="controls-button"
                     title="Play album"
                     on:click={handleAlbumPlay}>
                    <span class="icon-play"></span>
                    Play
                </div>
                <div class="controls-button"
                     title="Shuffle album"
                     on:click={handleAlbumShuffle}>
                    <span class="icon-shuffle"></span>
                    Shuffle
                </div>
            </div>
            <div class="items" class:items-playlist={!minimal}>
                {#each songs as song, idx (song.id)}
                    <SongItem item={song}
                              {minimal}
                              on:click={() => handleItemClick(song, idx)}
                              on:context-menu={handleItemContextMenu(song)} />
                {/each}
                {#if catalogAlbum}
                    <div class="found-container">
                        <div class="found-button" on:click|stopPropagation={handleCatalogAlbumClick}>
                            Open full catalog album...
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
{:catch error}
    Error: {error}
{/await}
</div>

<style>
    .overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.75);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .grid {
        display: grid;
        row-gap: 10px;
        column-gap: 20px;
        grid-template-rows: 1fr;
        grid-template-columns: 1fr 3fr;
        grid-template-areas:
            "art content";
        width: 90%;
        max-height: 90%;
        border-radius: 10px;
        box-shadow: var(--app-box-shadow);
        color: var(--chosen-fg-color, var(--album-modal-fg-color, var(--app-fg-color)));
        background-color: var(--chosen-modal-color, var(--album-modal-bg-color, var(--app-secondary-bg-color)));
        padding: 2%;
        box-sizing: border-box;
        overflow: hidden;

        --song-item-fg-color: var(--chosen-fg-color);
        --song-item-bg-color: var(--chosen-modal-color);
        --song-item-secondary-fg-color: var(--chosen-secondary-fg-color);
        --song-item-secondary-bg-color: var(--chosen-secondary-bg-color);
        --song-item-hover-fg-color: var(--chosen-bg-color);
        --song-item-hover-bg-color: var(--chosen-fg-color);
        --song-item-hover-secondary-fg-color: var(--chosen-hover-secondary-fg-color);
        --song-item-hover-secondary-bg-color: var(--chosen-hover-secondary-bg-color);
    }

    .art {
        grid-area: art;
    }

    .art > img {
        width: 100%;
        border-radius: 5px;
        box-shadow: var(--app-box-shadow);
    }

    .content {
        grid-area: content;
        display: grid;
        grid-gap: 10px;
        grid-template-rows: min-content min-content 1fr;
        grid-template-columns: 1fr;
        grid-template-areas: "infos" "controls" "items";
        width: 100%;
        /* height: 100%; */
        padding: 0 10px;
        box-sizing: border-box;
        overflow: hidden;
        overflow-y: auto;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        /* will-change: transform; */
    }

    .infos {
        grid-area: infos;
        box-sizing: border-box;
        overflow: hidden;
    }

    .title {
        font-size: 35px;
        font-weight: bold;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .artist {
        font-size: 20px;
        color: var(--album-modal-subtitle-fg-color, var(--chosen-secondary-fg-color, var(--app-secondary-fg-color)));
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .controls {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: repeat(2, min-content);
        grid-gap: 10px;
        align-items: center;
        justify-content: flex-start;
    }

    .controls-button {
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: repeat(2, min-content);
        grid-gap: 3px;
        align-items: center;
        justify-content: center;
        padding: 5px 10px;
        border-radius: 5px;
        box-sizing: border-box;
        font-size: 18px;
        font-weight: bold;
        color: var(--album-modal-control-fg-color, var(--chosen-bg-color, var(--app-bg-color)));
        background-color: var(--album-modal-control-bg-color, var(--chosen-fg-color, var(--app-fg-color)));
        cursor: pointer;
        transition: transform 0.1s;
    }

    .controls-button:hover {
        transform: scale(1.1);
    }

    .controls-button:active {
        transform: scale(0.9);
    }

    .items {
        grid-area: items;
    }

    .items-playlist {
        --song-item-padding: 7px;
    }

    .found-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .found-button {
        padding: 7.5px 20px;
        border-radius: 5px;
        border: solid 2px var(--chosen-fg-color, var(--app-fg-color));
        cursor: pointer;
        color: var(--chosen-fg-color, var(--app-fg-color));
        margin-top: 10px;
    }

    .found-button:hover {
        color: var(--chosen-bg-color, var(--app-inverse-fg-color));
        background-color: var(--chosen-fg-color, var(--app-inverse-bg-color));
        border-color: var(--chosen-fg-color, var(--app-inverse-fg-color));
    }

    @media screen and (max-width: 900px) {
        .grid {
            column-gap: 10px;

            grid-template-rows: repeat(4, min-content);
            grid-template-columns: 1fr;
            grid-template-areas: "art" "infos" "controls" "items";

            /* grid-template-rows: min-content min-content;
            grid-template-columns: 75px 1fr;
            grid-template-areas:
                "art      infos"
                "controls controls"
                "items    items"; */

            overflow: hidden;
            overflow-y: auto;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
    	    /* will-change: transform; */
            padding: 10px;
        }

        .title {
            font-size: 28px;
        }

        .artist {
            font-size: 18px;
        }

        .content {
            overflow: initial;
            scroll-behavior: initial;
            -webkit-overflow-scrolling: initial;
            will-change: initial;
            display: contents;
        }
    }
</style>
